import React, { forwardRef, useRef } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Icon } from "@xsolla/xui-primitives";
import { useResolvedTheme } from "@xsolla/xui-core";
import {
  ChevronDown,
  ChevronUp,
  Remove,
  ExclamationMarkCr,
} from "@xsolla/xui-icons-base";
import { useMultiSelectControl } from "./useMultiSelectControl";
import type { MultiSelectControlProps } from "./types";

export const MultiSelectControl = forwardRef<
  HTMLDivElement,
  MultiSelectControlProps
>(
  (
    {
      variant = "tag",
      flexible = true,
      onClick,
      removeValue,
      removeAllValues,
      size = "md",
      state: externalState,
      isError = false,
      isOpen = false,
      isFocus = false,
      stateList,
      selectedItems,
      removeTagsButtons = true,
      placeholder = "Select",
      iconLeft,
      iconRight,
      disabled = false,
      extraClear = false,
      width,
      themeMode,
      themeProductContext,
    },
    ref
  ) => {
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });
    const sizeStyles = theme.sizing.input(size);
    const inputColors = theme.colors.control.input;

    const state = externalState || (disabled ? "disable" : "default");
    const isDisable = state === "disable" || disabled;

    const containerRef = useRef<HTMLDivElement>(null);

    const { itemsRef, selectedContent, isCalculating } = useMultiSelectControl({
      variant,
      flexible,
      size,
      stateList,
      selectedItems,
      removeValue,
      placeholder,
      removeTagsButtons,
      disabled: isDisable,
      isCanBeCleared: Boolean(extraClear && selectedItems.length),
      containerRef,
    });

    const tagItemsGap = { xs: 4, sm: 4, md: 6, lg: 8, xl: 10 }[size];

    let backgroundColor = inputColors.bg;
    let borderColor = inputColors.border;

    if (isDisable) {
      backgroundColor = inputColors.bgDisable;
      borderColor = inputColors.borderDisable;
    } else if (isError) {
      borderColor = theme.colors.border.alert;
    } else if (isFocus || isOpen) {
      backgroundColor = theme.colors.control.focus.bg;
      borderColor = theme.colors.border.brand;
    } else if (state === "hover") {
      backgroundColor = inputColors.bgHover;
      borderColor = inputColors.borderHover;
    }

    const iconColor = isDisable ? inputColors.textDisable : inputColors.placeholder;

    return (
      <Box
        ref={ref}
        width={width}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        borderWidth={borderColor !== "transparent" ? 1 : 0}
        borderRadius={sizeStyles.radius}
        paddingVertical={sizeStyles.paddingVertical}
        paddingHorizontal={sizeStyles.paddingHorizontal}
        minHeight={sizeStyles.height}
        flexDirection="row"
        alignItems="center"
        gap={8}
        style={{
          cursor: isDisable ? "not-allowed" : onClick ? "pointer" : "default",
          boxSizing: "border-box",
          height: flexible ? "auto" : sizeStyles.height,
          position: "relative",
          zIndex: isOpen ? 1000 : undefined,
        }}
        onPress={isDisable ? undefined : onClick}
        hoverStyle={
          !isDisable && onClick && !isFocus && !isOpen && !isError
            ? {
                backgroundColor: inputColors.bgHover,
                borderColor: inputColors.borderHover,
              }
            : undefined
        }
      >
        <Box
          ref={containerRef}
          flex={1}
          flexDirection="row"
          alignItems="center"
          gap={8}
          style={{ minWidth: 0 }}
        >
          {iconLeft && (
            <Icon size={sizeStyles.iconSize} color={iconColor} variant="line">
              {iconLeft}
            </Icon>
          )}
          <Box
            ref={itemsRef}
            flex={1}
            flexDirection="row"
            flexWrap={flexible ? "wrap" : "nowrap"}
            alignItems="center"
            gap={variant === "tag" ? tagItemsGap : 4}
            style={{ overflow: "hidden", opacity: isCalculating ? 0 : 1 }}
          >
            {selectedContent}
          </Box>
        </Box>

        <Box flexDirection="row" alignItems="center" gap={4}>
          {extraClear && selectedItems.length > 0 && !isDisable && (
            <Box
              onPress={(e: React.MouseEvent) => {
                e.stopPropagation();
                removeAllValues();
              }}
              style={{ cursor: "pointer" }}
            >
              <Remove size={18} color={iconColor} variant="line" />
            </Box>
          )}
          {isError && (
            <ExclamationMarkCr size={18} color={theme.colors.content.alert.primary} />
          )}
          {iconRight ? (
            iconRight
          ) : isOpen ? (
            <ChevronUp size={18} color={iconColor} variant="line" />
          ) : (
            <ChevronDown size={18} color={iconColor} variant="line" />
          )}
        </Box>
      </Box>
    );
  }
);

MultiSelectControl.displayName = "MultiSelectControl";
